// src/components/layout/TopNav.jsx
import { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchUnreadCount } from '../../services/notificationsInboxService';
import { Button, Dropdown, Drawer, Space, Divider, Input, Tooltip } from 'antd';
import { useCart } from '../../context/CartContext';
import { ShoppingCartOutlined } from '@ant-design/icons';
import {
    SearchOutlined,
    MenuOutlined,
    GlobalOutlined,
    BulbOutlined,
    BulbFilled,
    UserOutlined,
    LogoutOutlined,
    DashboardOutlined,
    GiftOutlined,
    BellOutlined,
    CreditCardOutlined,
    WalletOutlined,
    InfoCircleOutlined,
    PhoneOutlined,
    FileTextOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { LANGUAGES } from '../../lib/i18n';
import { ROUTES } from '../../constants/routes';

export default function TopNav() {
    const { isDark, locale, setLocale, toggleMode } = useTheme();
    const { isAuthenticated, user, signOut } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();
    const { count } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [unread, setUnread] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!user?.id) { setUnread(0); return; }

        const load = () => {
            fetchUnreadCount(user.id).then(setUnread).catch(() => { });
        };

        load();
        intervalRef.current = setInterval(load, 60000); // refresh every 60 s

        const onFocus = () => load();
        window.addEventListener('focus', onFocus);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            window.removeEventListener('focus', onFocus);
        };
    }, [user?.id]);
    // Scroll state for soft nav tint
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close mobile drawer on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            navigate(`${ROUTES.MARKET}?q=${encodeURIComponent(query.trim())}`);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate(ROUTES.LOGIN, { replace: true });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    /* -------------------- Language dropdown -------------------- */
    const languageMenu = {
        items: LANGUAGES.map((l) => ({
            key: l.code,
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                    <span style={{ fontWeight: locale === l.code ? 700 : 500 }}>{l.nativeLabel}</span>
                    <span style={{ color: 'var(--home-fg-muted)', fontSize: 12 }}>{l.label}</span>
                    {locale === l.code && <span style={{ marginInlineStart: 'auto', color: 'var(--home-accent)' }}>✓</span>}
                </span>
            ),
            onClick: () => setLocale(l.code),
        })),
    };

    const currentLang = LANGUAGES.find((l) => l.code === locale) || LANGUAGES[0];

    /* -------------------- User dropdown -------------------- */
    const userMenu = {
        items: [
            { key: 'dashboard', icon: <DashboardOutlined />, label: <FormattedMessage id="nav.dashboard" />, onClick: () => navigate(ROUTES.DASHBOARD) },
            { key: 'profile', icon: <UserOutlined />, label: 'Profile', onClick: () => navigate(ROUTES.PROFILE) },
            { type: 'divider' },
            { key: 'logout', icon: <LogoutOutlined />, label: <FormattedMessage id="nav.logout" />, danger: true, onClick: handleSignOut },
        ],
    };

    /* -------------------- Desktop menu links -------------------- */
    const menuLinks = [
        { to: ROUTES.MARKET, id: 'nav.market', icon: <GiftOutlined /> },
        { to: ROUTES.ABOUT, id: 'footer.about', icon: <InfoCircleOutlined /> },
        { to: ROUTES.CONTACT, id: 'footer.contact', icon: <PhoneOutlined /> },
    ];

    return (
        <>
            <nav className={`nav ${scrolled ? 'is-scrolled' : ''}`}>
                {/* LEFT: brand + inline menu */}
                <div className="nav__left">
                    <button
                        type="button"
                        className="nav__burger"
                        aria-label="Open menu"
                        onClick={() => setMobileOpen(true)}
                    >
                        <MenuOutlined />
                    </button>

                    <Link to="/" className="nav__brand">
                        <FormattedMessage id="brand.name" />
                        <span><FormattedMessage id="brand.suffix" /></span>
                    </Link>

                    <div className="nav__menu">
                        {menuLinks.map((m) => (
                            <Link
                                key={m.to}
                                to={m.to}
                                className={location.pathname === m.to ? 'is-active' : ''}
                            >
                                {intl.formatMessage({ id: m.id })}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* RIGHT: search + language + theme + auth */}
                <div className="nav__right">
                    {/* Search (desktop) */}
                    <div className="nav__search">
                        <SearchOutlined />
                        <input
                            type="search"
                            placeholder={intl.formatMessage({ id: 'nav.search' })}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleSearch}
                            aria-label={intl.formatMessage({ id: 'nav.search' })}
                        />
                    </div>

                    {/* Language dropdown */}
                    <Dropdown menu={languageMenu} trigger={['click']} placement="bottomRight">
                        <button type="button" className="nav__lang" aria-label="Change language">
                            <GlobalOutlined />
                            <span>{currentLang.code.toUpperCase()}</span>
                        </button>
                    </Dropdown>

                    {/* Theme toggle */}
                    <Button
                        type="text"
                        shape="circle"
                        icon={isDark ? <BulbFilled /> : <BulbOutlined />}
                        onClick={toggleMode}
                        aria-label="Toggle theme"
                    />
                    <Button
                        type="text"
                        shape="circle"
                        className="notif-bell"
                        icon={<BellOutlined style={{ fontSize: 18 }} />}
                        onClick={() => navigate(ROUTES.NOTIFICATIONS_INBOX)}
                        aria-label="Notifications"
                    >
                        {unread > 0 && <span className="count">{unread > 99 ? '99+' : unread}</span>}
                    </Button>
                    <Tooltip title="Cart">
                        <Button
                            type="text"
                            shape="circle"
                            className="notif-bell"
                            icon={<ShoppingCartOutlined style={{ fontSize: 18 }} />}
                            onClick={() => navigate(ROUTES.CART)}
                            aria-label="Cart"
                        >
                            {count > 0 && <span className="count">{count > 99 ? '99+' : count}</span>}
                        </Button>
                    </Tooltip>
                    {/* Auth */}
                    {isAuthenticated ? (
                        <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
                            <Button type="primary" shape="round">
                                {user?.user_metadata?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Account'}
                            </Button>
                        </Dropdown>
                    ) : (
                        <Space size={8} className="nav__cta-desktop">
                            <Button onClick={() => navigate(ROUTES.LOGIN)}>
                                <FormattedMessage id="nav.signIn" />
                            </Button>
                            <Button type="primary" onClick={() => navigate(ROUTES.REGISTER)}>
                                <FormattedMessage id="nav.signUp" />
                            </Button>
                        </Space>
                    )}
                </div>
            </nav>

            {/* -------------------- Mobile drawer -------------------- */}
            <Drawer
                placement={document.documentElement.dir === 'rtl' ? 'right' : 'left'}
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                width={300}
                closable={false}
                styles={{ body: { padding: 20 } }}
                title={null}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Link to="/" className="nav__brand" onClick={() => setMobileOpen(false)}>
                        <FormattedMessage id="brand.name" />
                        <span><FormattedMessage id="brand.suffix" /></span>
                    </Link>
                    <Button type="text" onClick={() => setMobileOpen(false)}>✕</Button>
                </div>

                <Input
                    size="large"
                    prefix={<SearchOutlined />}
                    placeholder={intl.formatMessage({ id: 'nav.search' })}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onPressEnter={() => {
                        if (query.trim()) {
                            navigate(`${ROUTES.MARKET}?q=${encodeURIComponent(query.trim())}`);
                            setMobileOpen(false);
                        }
                    }}
                    style={{ marginBottom: 16 }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {menuLinks.map((m) => (
                        <Link
                            key={m.to}
                            to={m.to}
                            onClick={() => setMobileOpen(false)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '12px 10px',
                                borderRadius: 8,
                                color: 'var(--home-fg)',
                                fontSize: 15,
                                fontWeight: 500,
                                textDecoration: 'none',
                            }}
                        >
                            {m.icon} {intl.formatMessage({ id: m.id })}
                        </Link>
                    ))}

                    <Link
                        to={ROUTES.TERMS}
                        onClick={() => setMobileOpen(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', borderRadius: 8, color: 'var(--home-fg)', textDecoration: 'none' }}
                    >
                        <FileTextOutlined /> <FormattedMessage id="footer.terms" />
                    </Link>
                    <Link
                        to={ROUTES.PRIVACY}
                        onClick={() => setMobileOpen(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', borderRadius: 8, color: 'var(--home-fg)', textDecoration: 'none' }}
                    >
                        <SafetyCertificateOutlined /> <FormattedMessage id="footer.privacy" />
                    </Link>
                </div>

                <Divider />

                {isAuthenticated ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button block size="large" icon={<DashboardOutlined />} onClick={() => { navigate(ROUTES.DASHBOARD); setMobileOpen(false); }}>
                            <FormattedMessage id="nav.dashboard" />
                        </Button>
                        <Button block size="large" danger icon={<LogoutOutlined />} onClick={handleSignOut}>
                            <FormattedMessage id="nav.logout" />
                        </Button>
                    </Space>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button block size="large" onClick={() => { navigate(ROUTES.LOGIN); setMobileOpen(false); }}>
                            <FormattedMessage id="nav.signIn" />
                        </Button>
                        <Button block size="large" type="primary" onClick={() => { navigate(ROUTES.REGISTER); setMobileOpen(false); }}>
                            <FormattedMessage id="nav.signUp" />
                        </Button>
                    </Space>
                )}
            </Drawer>
        </>
    );
}